/**
 * Bengali number / date formatting helpers for the Job module.
 * The backend returns locale-agnostic values (ints, ISO dates); the UI is
 * Bengali-first, so we format here.
 */

// Default fallback logo shown for any job that has no uploaded logo.
export const DEFAULT_JOB_LOGO = '/images/govt-logo.webp';
// Resolve a job's logo, falling back to the default government logo.
export const jobLogoSrc = (job) => job?.logoUrl || DEFAULT_JOB_LOGO;

const BN_DIGITS = '০১২৩৪৫৬৭৮৯';

const BN_MONTHS = [
    'জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন',
    'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর',
];

/** Convert any number/string's Western digits to Bengali digits. */
export function toBnDigits(value) {
    return String(value ?? '').replace(/\d/g, (d) => BN_DIGITS[+d]);
}

/**
 * The other direction: Bengali digits back to Western ones, so a value that is
 * displayed in Bengali can still be searched or compared with a typed number.
 */
export function toEnDigits(value) {
    return String(value ?? '').replace(/[০-৯]/g, (d) => String(BN_DIGITS.indexOf(d)));
}

/** "৪০০০ জন" style vacancy label. */
export function formatVacancies(count) {
    return `${toBnDigits(count)} জন`;
}

/**
 * Format an ISO date as Bengali.
 * withYear=false → "১৫ জুন"; withYear=true → "৩০ জুন, ২০২৬".
 */
export function formatBnDate(input, { withYear = false } = {}) {
    if (!input) return '';
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) return '';
    const day = toBnDigits(date.getDate());
    const month = BN_MONTHS[date.getMonth()];
    const base = `${day} ${month}`;
    return withYear ? `${base}, ${toBnDigits(date.getFullYear())}` : base;
}

/** Whole days from now until the given date (can be negative if past). */
export function daysUntil(input) {
    if (!input) return 0;
    const date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) return 0;
    const ms = date.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
    return Math.round(ms / (24 * 60 * 60 * 1000));
}

/** "২ দিন বাকি" / "আজ শেষ" / "শেষ" label for the deadline. */
export function deadlineLabel(input) {
    const days = daysUntil(input);
    if (days < 0) return 'শেষ';
    if (days === 0) return 'আজ শেষ';
    return `${toBnDigits(days)} দিন বাকি`;
}

/**
 * Urgency tone derived from days remaining.
 * red ≤ 3 · gold 4–7 · green ≥ 8 (matches the original mockup).
 */
export function deadlineTone(input) {
    const days = daysUntil(input);
    if (days <= 3) return 'red';
    if (days <= 7) return 'gold';
    return 'green';
}
